import { useEffect, useState } from "react";
import { CheckCircle2, FileSpreadsheet, FileText, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/KpiCard";
import { PageHeader } from "@/components/FilterBar";
import { api, download, errMsg, moisLabel, money } from "@/lib/api";

const ANNEES = Array.from({ length: 4 }, (_, i) => String(new Date().getFullYear() - i));
const fr = (v) => (v === null || v === undefined ? "" : Number(v).toFixed(2).replace(".", ","));
const nb = (v) => (String(v).trim() ? Number(String(v).replace(/\s/g, "").replace(",", ".")) : null);

// Solde imprimé sur le relevé papier ou PDF, enregistré en quittant le champ
const SoldeImprime = ({ m, onChange }) => {
  const [v, setV] = useState(fr(m.solde_imprime));
  const enregistrer = async () => {
    const montant = nb(v);
    if (montant === (m.solde_imprime ?? null)) { return; }
    if (montant !== null && Number.isNaN(montant)) { toast.error("Montant invalide"); return; }
    try { await api.put("/conciliation-bancaire/solde", { mois: m.mois, montant }); toast.success("Solde du relevé enregistré"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={enregistrer} onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
    inputMode="decimal" placeholder="relevé" className="h-8 w-28 text-right tabular" data-testid={`banque-${m.mois}-imprime`} />;
};

const Ligne = ({ m, onChange }) => (
  <tr className={`border-t border-slate-100 align-top ${m.ok ? "" : "bg-red-50/60"}`} data-testid={`banque-${m.mois}`}>
    <td className="py-2 pr-2 whitespace-nowrap font-medium">{moisLabel(m.mois)}</td>
    {["debut", "depots", "retraits", "fin_calculee", "fin_releve"].map((k) => <td key={k} className="py-2 px-2 text-right tabular whitespace-nowrap" data-testid={`banque-${m.mois}-${k}`}>{m[k] === null ? "—" : money(m[k])}</td>)}
    <td className="py-1 px-2"><SoldeImprime key={m.solde_imprime ?? ""} m={m} onChange={onChange} /></td>
    <td className="py-2 pl-2 text-xs" data-testid={`banque-${m.mois}-statut`}>
      {m.ok ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />Balance</span>
        : <span className="inline-flex items-start gap-1 text-red-700"><TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />{m.problemes.join(" ; ")}</span>}
    </td>
  </tr>
);

// Conciliation bancaire mensuelle : solde début + dépôts − retraits = solde fin
export default function ConciliationBancaire() {
  const [annee, setAnnee] = useState(ANNEES[0]);
  const [d, setD] = useState(null);
  const [v, setV] = useState(0);
  useEffect(() => { api.get("/conciliation-bancaire", { params: { annee } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [annee, v]);
  const exporter = (format) => download("/conciliation-bancaire/export", { annee, format }, `Conciliation bancaire ${annee}.${format}`).catch((e) => toast.error(errMsg(e)));
  const action = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={annee} onValueChange={(a) => { setD(null); setAnnee(a); }}>
        <SelectTrigger className="h-8 w-24" data-testid="banque-annee"><SelectValue /></SelectTrigger>
        <SelectContent>{ANNEES.map((a) => <SelectItem key={a} value={a} data-testid={`banque-annee-${a}`}>{a}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={() => exporter("pdf")} data-testid="banque-pdf"><FileText className="h-3.5 w-3.5 mr-1" />PDF</Button>
      <Button size="sm" variant="outline" onClick={() => exporter("xlsx")} data-testid="banque-excel"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Excel</Button>
    </div>
  );
  return (
    <div data-testid="banque-page">
      <PageHeader eyebrow="Banque" title="Conciliation bancaire" />
      <Panel title={`Mois ${annee}`} action={action} testId="banque-panel">
        <p className="mb-3 text-xs text-slate-500">Calcul à partir du solde inscrit sur chaque opération des relevés Desjardins importés. Un mois ne balance pas si le calcul ne retombe pas sur le dernier solde, si le solde de début ne suit pas la fin du mois précédent (opérations manquantes), ou s'il diffère du solde imprimé que vous entrez.</p>
        {!d ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : !d.mois.length ? <p className="text-sm text-slate-500" data-testid="banque-aucun">Aucun relevé importé pour {annee}.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
                {["Mois", "Solde début", "Dépôts", "Retraits", "Fin calculée", "Fin (relevé importé)", "Solde imprimé", "Statut"].map((h, i) => <th key={h} className={`pb-1 px-2 whitespace-nowrap ${i && i < 7 ? "text-right" : "text-left"}`}>{h}</th>)}
              </tr></thead>
              <tbody>{d.mois.map((m) => <Ligne key={m.mois} m={m} onChange={() => setV((x) => x + 1)} />)}</tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
